import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc } from 'firebase/firestore';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import { useAuth } from '../../../../auth/AuthContext';
import { db } from '../../../../firebase-client';
import {
  createEventComment,
  deleteMyBoardContent,
  ensureBoardModerationCase,
  getEventCommentThread,
  reviewBoardModerationCase,
  searchBoardMentionTargets,
  setEventCommentsEnabled,
  updateBoardReply,
  updateBoardTopic,
} from '../../../../utils/boardApi';
import { boardHtmlToText, sanitizeBoardHtml } from '../../../../utils/boardModeration';
import { reportCaughtError } from '../../../../utils/clientErrorReporting';
import { readOfflineContentCache, writeOfflineContentCache } from '../../../../utils/offlineContentCache';
import BoardReactionBar from '../../../messages/BoardReactionBar';
import BoardRichContent from '../../../messages/BoardRichContent';
import RichTextEditor from '../../../messages/boards/RichTextEditor';
import '../../../../styles/messages.css';

const BOARD_ID = 'events';
const CACHE_SCOPE = 'eventComments';
const EMPTY_MENTION_METADATA = { mentions: [], mentionsBoard: false, mentionTags: [] };
const LOG_PREFIX = '[EventComments]';

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function formatWhen(value) {
  const millis = toMillis(value);
  if (!millis) return '—';
  const date = new Date(millis);
  if (Number.isNaN(date.getTime())) return '—';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const absDiffMs = Math.abs(diffMs);
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;

  if (absDiffMs < minuteMs) return 'just now';
  if (absDiffMs < hourMs) {
    const minutes = Math.max(1, Math.round(absDiffMs / minuteMs));
    return diffMs >= 0 ? `${minutes} minute${minutes === 1 ? '' : 's'} ago` : `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  if (date.toDateString() === now.toDateString()) {
    const hours = Math.max(1, Math.round(absDiffMs / hourMs));
    return diffMs >= 0 ? `${hours} hour${hours === 1 ? '' : 's'} ago` : `in ${hours} hour${hours === 1 ? '' : 's'}`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `yesterday @ ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }

  return `${date.toLocaleDateString([], { day: '2-digit', month: 'short' })} @ ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function eventDocId(eventId, occurrenceId) {
  return occurrenceId ? `${eventId}__${occurrenceId}` : eventId;
}

function reportEventCommentError(error, context = {}) {
  reportCaughtError(error, {
    source: 'event-comments',
    severity: 'error',
    context,
  });
}

function renderContent(content) {
  return <BoardRichContent html={content?.bodyHtml || ''} />;
}

function ActionMenu({ label = 'More actions', children }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={menuRef} className={`board-action-menu ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className="board-action-menu__trigger"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        ...
      </button>
      {open && (
        <div className="board-action-menu__panel" role="menu">
          {children}
        </div>
      )}
    </div>
  );
}

function ActionMenuItem({ children, onClick, disabled = false }) {
  return (
    <button
      type="button"
      className="board-action-menu__item"
      role="menuitem"
      disabled={disabled}
      onClick={(event) => {
        event.currentTarget.closest('.board-action-menu')?.querySelector('.board-action-menu__trigger')?.click();
        onClick?.();
      }}
    >
      {children}
    </button>
  );
}

export default function EventCommentsSection({
  teamId,
  eventId,
  occurrenceId = null,
  commentsEnabled = false,
  isAdmin = false,
  canModerateComments = false,
  onCommentsEnabledChange,
}) {
  const { user } = useAuth();
  const [topic, setTopic] = useState(null);
  const [replies, setReplies] = useState([]);
  const [draft, setDraft] = useState('');
  const [mentionMetadata, setMentionMetadata] = useState(EMPTY_MENTION_METADATA);
  const [editingComment, setEditingComment] = useState(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [editingMentionMetadata, setEditingMentionMetadata] = useState(EMPTY_MENTION_METADATA);
  const [posting, setPosting] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [error, setError] = useState('');

  const resolvedTopicId = eventDocId(eventId, occurrenceId);
  const currentUserId = user?.uid || '';
  const canShow = Boolean(teamId && eventId && (commentsEnabled || isAdmin));
  const canLoadComments = Boolean(teamId && eventId && commentsEnabled);
  const canPost = Boolean(commentsEnabled && teamId && eventId && !posting && !busyAction);

  const loadThreadFallback = useCallback(async () => {
    if (!teamId || !eventId || !commentsEnabled || !resolvedTopicId) return false;
    const result = await getEventCommentThread({
      teamId,
      eventId,
      occurrenceId,
    });
    const nextTopic = result?.topic || null;
    const nextReplies = Array.isArray(result?.replies) ? result.replies : [];
    setTopic(nextTopic);
    setReplies(nextReplies);
    writeOfflineContentCache([CACHE_SCOPE, 'topic', teamId, resolvedTopicId], nextTopic);
    writeOfflineContentCache([CACHE_SCOPE, 'replies', teamId, resolvedTopicId], nextReplies.slice(-200));
    setError('');
    return true;
  }, [commentsEnabled, eventId, occurrenceId, resolvedTopicId, teamId]);

  useEffect(() => {
    if (!canLoadComments || !resolvedTopicId) {
      setTopic(null);
      return undefined;
    }
    const topicPath = `teams/${teamId}/boards/${BOARD_ID}/topics/${resolvedTopicId}`;
    console.info(`${LOG_PREFIX} subscribing to topic`, {
      teamId,
      eventId,
      occurrenceId,
      commentsEnabled,
      isAdmin,
      topicId: resolvedTopicId,
      path: topicPath,
    });
    const topicCacheParts = [CACHE_SCOPE, 'topic', teamId, resolvedTopicId];
    setTopic(readOfflineContentCache(topicCacheParts, null));
    return safeOnSnapshot(
      doc(db, 'teams', teamId, 'boards', BOARD_ID, 'topics', resolvedTopicId),
      (snap) => {
        console.info(`${LOG_PREFIX} topic snapshot`, {
          path: topicPath,
          exists: snap.exists(),
          id: snap.id,
          kind: snap.exists() ? snap.data()?.kind : null,
          eventDocId: snap.exists() ? snap.data()?.eventDocId : null,
          replyCount: snap.exists() ? snap.data()?.replyCount : null,
        });
        setError('');
        const nextTopic = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setTopic(nextTopic);
        writeOfflineContentCache(topicCacheParts, nextTopic);
      },
      (err) => {
        console.error(`${LOG_PREFIX} topic listener failed`, {
          path: topicPath,
          code: err?.code,
          message: err?.message,
          teamId,
          eventId,
          occurrenceId,
          topicId: resolvedTopicId,
        }, err);
        loadThreadFallback().catch((fallbackErr) => {
          reportEventCommentError(fallbackErr, {
            phase: 'topic-fallback',
            teamId,
            eventId,
            occurrenceId: occurrenceId || '',
            topicId: resolvedTopicId,
            path: topicPath,
            code: fallbackErr?.code || '',
            listenerCode: err?.code || '',
          });
          setTopic(readOfflineContentCache(topicCacheParts, null));
          setError('Could not load comments for this event.');
        });
      },
      { label: 'event-comments-topic', teamId, suppressPermissionDenied: true }
    );
  }, [canLoadComments, commentsEnabled, eventId, isAdmin, loadThreadFallback, occurrenceId, resolvedTopicId, teamId]);

  useEffect(() => {
    if (!canLoadComments || !resolvedTopicId || !topic) {
      setReplies([]);
      return undefined;
    }
    const repliesPath = `teams/${teamId}/boards/${BOARD_ID}/topics/${resolvedTopicId}/replies`;
    console.info(`${LOG_PREFIX} subscribing to replies`, {
      teamId,
      eventId,
      occurrenceId,
      topicId: resolvedTopicId,
      path: repliesPath,
    });
    const repliesCacheParts = [CACHE_SCOPE, 'replies', teamId, resolvedTopicId];
    setReplies(readOfflineContentCache(repliesCacheParts, []));
    return safeOnSnapshot(
      collection(db, 'teams', teamId, 'boards', BOARD_ID, 'topics', resolvedTopicId, 'replies'),
      (snap) => {
        console.info(`${LOG_PREFIX} replies snapshot`, {
          path: repliesPath,
          size: snap.size,
        });
        setError('');
        const nextReplies = snap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((reply) => reply.moderationStatus !== 'deleted')
          .sort((left, right) => toMillis(left.createdAt) - toMillis(right.createdAt));
        setReplies(nextReplies);
        writeOfflineContentCache(repliesCacheParts, nextReplies.slice(-200));
      },
      (err) => {
        console.error(`${LOG_PREFIX} replies listener failed`, {
          path: repliesPath,
          code: err?.code,
          message: err?.message,
          teamId,
          eventId,
          occurrenceId,
          topicId: resolvedTopicId,
        }, err);
        loadThreadFallback().catch((fallbackErr) => {
          reportEventCommentError(fallbackErr, {
            phase: 'replies-fallback',
            teamId,
            eventId,
            occurrenceId: occurrenceId || '',
            topicId: resolvedTopicId,
            path: repliesPath,
            code: fallbackErr?.code || '',
            listenerCode: err?.code || '',
          });
          setReplies(readOfflineContentCache(repliesCacheParts, []));
          setError('Could not load comments for this event.');
        });
      },
      { label: 'event-comments-replies', teamId, suppressPermissionDenied: true }
    );
  }, [canLoadComments, eventId, loadThreadFallback, occurrenceId, resolvedTopicId, teamId, topic]);

  const visibleReplies = useMemo(
    () => replies.filter((reply) => isAdmin || reply.moderationStatus !== 'hidden'),
    [isAdmin, replies]
  );

  const handleSearchMentionTargets = useCallback(async ({ query = '' } = {}) => {
    if (!teamId || !eventId || !commentsEnabled) return [];
    try {
      const result = await searchBoardMentionTargets({
        teamId,
        boardId: BOARD_ID,
        eventId,
        occurrenceId,
        query,
      });
      return Array.isArray(result?.targets) ? result.targets : [];
    } catch (err) {
      reportEventCommentError(err, {
        phase: 'mention-targets',
        teamId,
        eventId,
        occurrenceId: occurrenceId || '',
        queryLength: String(query || '').length,
      });
      return [];
    }
  }, [commentsEnabled, eventId, occurrenceId, teamId]);

  const beginEdit = (content, contentType) => {
    if (!content?.id) return;
    setEditingComment({ id: content.id, contentType });
    setEditingDraft(content.bodyHtml || '');
    setEditingMentionMetadata(EMPTY_MENTION_METADATA);
    setError('');
  };

  const cancelEdit = () => {
    setEditingComment(null);
    setEditingDraft('');
    setEditingMentionMetadata(EMPTY_MENTION_METADATA);
  };

  const handleUpdateComment = async () => {
    if (!editingComment || !teamId || !resolvedTopicId || busyAction) return;
    const safeHtml = sanitizeBoardHtml(editingDraft);
    if (!boardHtmlToText(safeHtml)) {
      setError('Comment content is required.');
      return;
    }

    setBusyAction('update-comment');
    setError('');
    try {
      const payload = {
        teamId,
        boardId: BOARD_ID,
        topicId: resolvedTopicId,
        bodyHtml: safeHtml,
        mentionMetadata: editingMentionMetadata,
        warningShown: false,
      };
      if (editingComment.contentType === 'topic') {
        await updateBoardTopic(payload);
      } else {
        await updateBoardReply({
          ...payload,
          replyId: editingComment.id,
        });
      }
      await loadThreadFallback();
      cancelEdit();
    } catch (err) {
      reportEventCommentError(err, {
        phase: 'update-comment',
        teamId,
        eventId,
        occurrenceId: occurrenceId || '',
        topicId: resolvedTopicId,
        contentType: editingComment.contentType,
      });
      setError(err?.message || 'Could not update comment.');
    } finally {
      setBusyAction('');
    }
  };

  const handleDeleteComment = async (content, contentType) => {
    if (!content?.id || !teamId || !resolvedTopicId || !canModerateComments || busyAction) return;
    if (!window.confirm('Remove this comment from club discussion? Member Nest support will retain the original content for review.')) return;

    const contentId = contentType === 'topic' ? resolvedTopicId : content.id;
    setBusyAction(`delete-${contentType}-${contentId}`);
    setError('');
    try {
      const result = await ensureBoardModerationCase({
        teamId,
        boardId: BOARD_ID,
        topicId: resolvedTopicId,
        contentType,
        contentId,
      });
      await reviewBoardModerationCase({
        teamId,
        boardId: BOARD_ID,
        caseId: result.caseId,
        action: 'delete_content',
        reviewScope: 'club',
      });
      if (contentType === 'topic') {
        setTopic(null);
      } else {
        setReplies((current) => current.filter((reply) => reply.id !== content.id));
      }
      await loadThreadFallback();
      if (editingComment?.id === content.id) cancelEdit();
    } catch (err) {
      reportEventCommentError(err, {
        phase: 'moderation-delete-comment',
        teamId,
        eventId,
        occurrenceId: occurrenceId || '',
        topicId: resolvedTopicId,
        contentType,
      });
      setError(err?.message || 'Could not delete comment.');
    } finally {
      setBusyAction('');
    }
  };

  const handleDeleteMyComment = async (content, contentType) => {
    if (!content?.id || !teamId || !resolvedTopicId || busyAction) return;
    const confirmMessage = contentType === 'topic'
      ? 'Remove this comment thread from club discussion? The original content will be retained for moderation evidence.'
      : 'Remove this comment from club discussion? The original content will be retained for moderation evidence.';
    if (!window.confirm(confirmMessage)) return;

    const contentId = contentType === 'topic' ? resolvedTopicId : content.id;
    setBusyAction(`self-delete-${contentType}-${contentId}`);
    setError('');
    try {
      await deleteMyBoardContent({
        teamId,
        boardId: BOARD_ID,
        topicId: resolvedTopicId,
        contentType,
        contentId,
      });
      if (contentType === 'topic') {
        setTopic(null);
        setReplies([]);
      } else {
        setReplies((current) => current.filter((reply) => reply.id !== content.id));
      }
      await loadThreadFallback();
      if (editingComment?.id === content.id) cancelEdit();
    } catch (err) {
      reportEventCommentError(err, {
        phase: 'self-delete-comment',
        teamId,
        eventId,
        occurrenceId: occurrenceId || '',
        topicId: resolvedTopicId,
        contentType,
      });
      setError(err?.message || 'Could not delete comment.');
    } finally {
      setBusyAction('');
    }
  };

  const renderCommentActions = (content, contentType) => {
    if (!content?.id || content.moderationStatus === 'deleted') return null;
    const canEdit = Boolean(
      commentsEnabled
      && currentUserId
      && content.authorId === currentUserId
      && content.moderationStatus !== 'hidden'
    );
    const canSelfDelete = Boolean(
      commentsEnabled
      && currentUserId
      && content.authorId === currentUserId
      && !canModerateComments
      && content.moderationStatus !== 'deleted'
    );
    const canDelete = Boolean(canModerateComments && content.moderationStatus !== 'deleted');
    if (!canEdit && !canSelfDelete && !canDelete) return null;

    return (
      <div className="board-thread-card__actions">
        <ActionMenu label="Comment actions">
          {canEdit && (
            <ActionMenuItem onClick={() => beginEdit(content, contentType)}>
              Edit Comment
            </ActionMenuItem>
          )}
          {canDelete && (
            <ActionMenuItem
              disabled={busyAction === `delete-${contentType}-${contentType === 'topic' ? resolvedTopicId : content.id}`}
              onClick={() => handleDeleteComment(content, contentType)}
            >
              Delete Comment
            </ActionMenuItem>
          )}
          {canSelfDelete && (
            <ActionMenuItem
              disabled={busyAction === `self-delete-${contentType}-${contentType === 'topic' ? resolvedTopicId : content.id}`}
              onClick={() => handleDeleteMyComment(content, contentType)}
            >
              Delete Comment
            </ActionMenuItem>
          )}
        </ActionMenu>
      </div>
    );
  };

  const renderEditableContent = (content, contentType) => {
    const editingThis = editingComment?.contentType === contentType && editingComment?.id === content?.id;
    if (!editingThis) return renderContent(content);

    return (
      <>
        <RichTextEditor
          value={editingDraft}
          onChange={setEditingDraft}
          onMentionMetadataChange={setEditingMentionMetadata}
          searchMentionTargets={handleSearchMentionTargets}
          minHeight={contentType === 'topic' ? 160 : 140}
        />
        <div className="board-inline-actions">
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={handleUpdateComment}
            disabled={busyAction === 'update-comment'}
          >
            {busyAction === 'update-comment' ? 'Saving...' : 'Save Comment'}
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={cancelEdit}
          >
            Cancel
          </button>
        </div>
      </>
    );
  };

  const handlePost = async () => {
    if (!canPost) return;
    const safeHtml = sanitizeBoardHtml(draft);
    if (!boardHtmlToText(safeHtml)) {
      setError('Comment content is required.');
      return;
    }
    setPosting(true);
    setError('');
    try {
      console.info(`${LOG_PREFIX} posting comment`, {
        teamId,
        eventId,
        occurrenceId,
        topicId: resolvedTopicId,
      });
      const result = await createEventComment({
        teamId,
        eventId,
        occurrenceId,
        bodyHtml: safeHtml,
        mentionMetadata,
        warningShown: false,
      });
      console.info(`${LOG_PREFIX} post complete`, {
        teamId,
        eventId,
        occurrenceId,
        topicId: resolvedTopicId,
        result,
      });
      await loadThreadFallback();
      setDraft('');
      setMentionMetadata(EMPTY_MENTION_METADATA);
    } catch (err) {
      console.error(`${LOG_PREFIX} post failed`, {
        teamId,
        eventId,
        occurrenceId,
        topicId: resolvedTopicId,
        code: err?.code,
        message: err?.message,
      }, err);
      reportEventCommentError(err, {
        phase: 'post-comment',
        teamId,
        eventId,
        occurrenceId: occurrenceId || '',
        topicId: resolvedTopicId,
        code: err?.code || '',
      });
      setError(err?.message || 'Could not post comment.');
    } finally {
      setPosting(false);
    }
  };

  if (!canShow) return null;

  if (!commentsEnabled) {
    const handleEnableComments = async () => {
      if (!isAdmin || !teamId || enabling) return;
      setEnabling(true);
      setError('');
      try {
        await setEventCommentsEnabled({ teamId, enabled: true });
        onCommentsEnabledChange?.(true);
        window.alert('Event comments are now on for this club. You can turn them off again from Event Setup.');
      } catch (err) {
        reportEventCommentError(err, {
          phase: 'enable-comments',
          teamId,
          eventId,
          occurrenceId: occurrenceId || '',
          topicId: resolvedTopicId,
          code: err?.code || '',
        });
        setError(err?.message || 'Could not turn on event comments.');
      } finally {
        setEnabling(false);
      }
    };

    return (
      <section className="board-compose-card board-compose-card--inline event-comments-section">
        <div className="board-compose-card__header">
          <div>
            <strong>Comments</strong>
            <p className="text-muted">Comments are currently off.</p>
          </div>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={handleEnableComments}
            disabled={enabling}
          >
            {enabling ? 'Turning on...' : 'Turn On Comments'}
          </button>
        </div>
        {error && <p className="text-danger">{error}</p>}
      </section>
    );
  }

  return (
    <section className="board-compose-card board-compose-card--inline event-comments-section">
      <div className="board-compose-card__header">
        <div>
          <strong>Comments</strong>
          <p className="text-muted">Visible to members who can access this event.</p>
        </div>
      </div>

      {error && <p className="text-danger">{error}</p>}

      {!topic ? (
        <div className="board-thread-stream__empty">
          <strong>No comments yet</strong>
          <p className="text-muted">
            {commentsEnabled ? 'Start the discussion for this event.' : 'Turn comments on to start a discussion.'}
          </p>
        </div>
      ) : (
        <div className="board-thread-stream__list event-comments-section__thread">
          {topic.moderationStatus !== 'deleted' && (topic.moderationStatus !== 'hidden' || isAdmin) ? (
            <article className="board-thread-card board-thread-card--topic">
              <div className="board-reply-card__body-row">
                <div className="board-reply-card__content">{renderEditableContent(topic, 'topic')}</div>
                {renderCommentActions(topic, 'topic')}
              </div>
              <div className="board-reply-card__meta">
                <span>{topic.authorName || 'Member'}</span>
                <span>{formatWhen(topic.createdAt)}</span>
              </div>
              {!(editingComment?.contentType === 'topic' && editingComment?.id === topic.id) && (
                <BoardReactionBar
                  teamId={teamId}
                  boardId={BOARD_ID}
                  topicId={resolvedTopicId}
                  contentType="topic"
                  contentId={topic.id}
                  content={topic}
                  currentUserId={currentUserId}
                  canReact={canPost}
                />
              )}
            </article>
          ) : null}

          {visibleReplies.map((reply) => (
            <article key={reply.id} className="board-thread-card board-thread-card--reply">
              <div className="board-reply-card__body-row">
                <div className="board-reply-card__content">{renderEditableContent(reply, 'reply')}</div>
                {renderCommentActions(reply, 'reply')}
              </div>
              <div className="board-reply-card__meta">
                <span>{reply.authorName || 'Member'}</span>
                <span>{formatWhen(reply.createdAt)}</span>
              </div>
              {!(editingComment?.contentType === 'reply' && editingComment?.id === reply.id) && (
                <BoardReactionBar
                  teamId={teamId}
                  boardId={BOARD_ID}
                  topicId={resolvedTopicId}
                  contentType="reply"
                  contentId={reply.id}
                  content={reply}
                  currentUserId={currentUserId}
                  canReact={canPost}
                />
              )}
            </article>
          ))}
        </div>
      )}

      {commentsEnabled && (
        <div className="event-comments-section__composer">
          <RichTextEditor
            value={draft}
            onChange={setDraft}
            onMentionMetadataChange={setMentionMetadata}
            searchMentionTargets={handleSearchMentionTargets}
            minHeight={140}
            placeholder={topic ? 'Add a comment...' : 'Start the comments...'}
          />
          <div className="board-compose-card__footer">
            <div className="text-muted board-compose-card__hint">
              Comments use the same moderation tools as message boards.
            </div>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={handlePost}
              disabled={posting || !draft.trim()}
            >
              {posting ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
